import React, { Fragment, Suspense, lazy } from 'react';
import { Router, Route, Switch, Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { createBrowserHistory } from 'history';
import { connect } from 'react-redux';

import { getNotificationCount } from '../state/services/zendeskService';

import { PrivateRoute } from '../shared/PrivateRoute/PrivateRoute';
import { AdminPrivateRoute } from '../shared/AdminPrivateRoute/AdminPrivateRoute';

import LazyMount from '../components/LazyMount/LazyMount';
import NotificationSlider from '../components/Sliders/NotificationSlider';
import AppChrome from '../pages/landing/AppChrome';

const history = createBrowserHistory();
const ChatWidget = lazy(() => import(/* webpackChunkName: 'chat-widget' */ '../components/Chat/ChatWidget'));

// User pages
const Header = lazy(() => import(/* webpackChunkName: 'layout-header' */ '../components/Header/Header'));
const Footer = lazy(() => import(/* webpackChunkName: 'layout-footer' */ '../components/Footer/Footer'));

const HomePage = lazy(() => import(/* webpackChunkName: 'page-home' */ '../pages/HomePage'));
const LoginPage = lazy(() => import(/* webpackChunkName: 'page-login' */ '../pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import(/* webpackChunkName: 'page-forgot-password' */ '../pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import(/* webpackChunkName: 'page-reset-password' */ '../pages/ResetPasswordPage'));

const BokPage = lazy(() => import(/* webpackChunkName: 'page-bok' */ '../pages/BokPage'));
const FilesPage = lazy(() => import(/* webpackChunkName: 'page-files' */ '../pages/FilesPage'));
const BokPricePage = lazy(() => import(/* webpackChunkName: 'page-bok-price' */ '../pages/customer/BokPricePage'));
const BidPricePage = lazy(() => import(/* webpackChunkName: 'page-bid-price' */ '../pages/customer/BidPricePage'));
const BokStatusPage = lazy(() => import(/* webpackChunkName: 'page-bok-status' */ '../pages/customer/BokStatusPage'));
const LabelPage = lazy(() => import(/* webpackChunkName: 'page-label' */ '../pages/customer/LabelPage'));
const WarehouseLabelPage = lazy(() => import(/* webpackChunkName: 'page-warehouse-label' */ '../pages/customer/WarehouseLabelPage'));
const UnsubscribePage = lazy(() => import(/* webpackChunkName: 'page-unsubscribe' */ '../pages/UnsubscribePage'));

const BookingPage = lazy(() => import(/* webpackChunkName: 'page-booking' */ '../pages/BookingPage'));
const NewBookingPage = lazy(() => import(/* webpackChunkName: 'page-booking' */ '../pages/NewBookingPage'));
const BookingLinesPage = lazy(() => import(/* webpackChunkName: 'page-booking-lines' */ '../pages/BookingLinesPage'));
const BookingLineDetailsPage = lazy(() => import(/* webpackChunkName: 'page-booking-line-details' */ '../pages/BookingLineDetailsPage'));
const AllBookingsPage = lazy(() => import(/* webpackChunkName: 'page-all-bookings' */ '../pages/AllBookingsPage'));
const ConsolidationWorkbenchPage = lazy(() => import(/* webpackChunkName: 'page-consolidation-workbench' */ '../pages/ConsolidationWorkbenchPage'));
const ConsolidationClusterDetailPage = lazy(() => import(/* webpackChunkName: 'page-consolidation-cluster' */ '../pages/ConsolidationClusterDetailPage'));
const BookingSetsPage = lazy(() => import(/* webpackChunkName: 'page-booking-sets' */ '../pages/BookingSets/BookingSetsList'));
const BookingSetsViewPage = lazy(() => import(/* webpackChunkName: 'page-booking-sets-view' */ '../pages/BookingSets/BookingSetsView'));

const UploadPage = lazy(() => import(/* webpackChunkName: 'page-upload' */ '../pages/UploadPage'));
const PodsPage = lazy(() => import(/* webpackChunkName: 'page-pods' */ '../pages/PodsPage'));
const ReportPage = lazy(() => import(/* webpackChunkName: 'page-report' */ '../pages/report/ReportPage'));

// Admin pages
const Main = lazy(() => import(/* webpackChunkName: 'admin-main' */ '../pages/admin/Main'));
const Login = lazy(() => import(/* webpackChunkName: 'admin-login' */ '../pages/admin/AdminViews/Login'));
const Dashboard = lazy(() => import(/* webpackChunkName: 'admin-dashboard' */ '../pages/admin/AdminViews/Dashboard'));

const TotalDeliveries = lazy(() => import(/* webpackChunkName: 'admin-total-deliveries' */ '../pages/admin/AdminViews/Chart/TotalDeliveries'));
const OnTimeLateDeliveries = lazy(() => import(/* webpackChunkName: 'admin-ontime-late' */ '../pages/admin/AdminViews/Chart/OnTimeLateDeliveries'));
const DeliveriesByClient = lazy(() => import(/* webpackChunkName: 'admin-deliveries-by-client' */ '../pages/admin/AdminViews/Chart/DeliveriesByClient'));
const ActiveBookings = lazy(() => import(/* webpackChunkName: 'admin-active-bookings' */ '../pages/admin/AdminViews/Chart/ActiveBookings'));

const EmailTemplates = lazy(() => import(/* webpackChunkName: 'admin-email-templates' */ '../pages/admin/AdminViews/EmailTemplates/EmailTemplates'));
const AddEmailTemplates = lazy(() => import(/* webpackChunkName: 'admin-email-templates-add' */ '../pages/admin/AdminViews/EmailTemplates/AddEmailTemplates'));
const EditEmailTemplates = lazy(() => import(/* webpackChunkName: 'admin-email-templates-edit' */ '../pages/admin/AdminViews/EmailTemplates/EditEmailTemplates'));

const Users = lazy(() => import(/* webpackChunkName: 'admin-users' */ '../pages/admin/AdminViews/Users/Users'));
const AddUser = lazy(() => import(/* webpackChunkName: 'admin-users-add' */ '../pages/admin/AdminViews/Users/AddUser'));
const EditUser = lazy(() => import(/* webpackChunkName: 'admin-users-edit' */ '../pages/admin/AdminViews/Users/EditUser'));

const FreightProviders = lazy(() => import(/* webpackChunkName: 'admin-freight-providers' */ '../pages/admin/AdminViews/FreightProviders/FreightProviders'));
const AddFreightProviders = lazy(() => import(/* webpackChunkName: 'admin-freight-providers-add' */ '../pages/admin/AdminViews/FreightProviders/AddFreightProviders'));
const EditFreightProviders = lazy(() => import(/* webpackChunkName: 'admin-freight-providers-edit' */ '../pages/admin/AdminViews/FreightProviders/EditFreightProviders'));
const StatusMapping = lazy(() => import(/* webpackChunkName: 'admin-status-mapping' */ '../pages/admin/AdminViews/FreightProviders/StatusMapping'));

const PricingOnlyList = lazy(() => import(/* webpackChunkName: 'admin-pricing-only-list' */ '../pages/admin/AdminViews/PricingOnly/List'));
const PricingOnlyUpload = lazy(() => import(/* webpackChunkName: 'admin-pricing-only-upload' */ '../pages/admin/AdminViews/PricingOnly/Upload'));

const PricingRuleList = lazy(() => import(/* webpackChunkName: 'admin-pricing-rule-list' */ '../pages/admin/AdminViews/PricingRule/List'));
const PricingRuleUpload = lazy(() => import(/* webpackChunkName: 'admin-pricing-rule-upload' */ '../pages/admin/AdminViews/PricingRule/Upload'));
const PricingRuleStatus = lazy(() => import(/* webpackChunkName: 'admin-pricing-rule-status' */ '../pages/admin/AdminViews/PricingRule/Status'));

const Vehicles = lazy(() => import(/* webpackChunkName: 'admin-vehicles' */ '../pages/admin/AdminViews/Vehicles/Vehicles'));
const Timings = lazy(() => import(/* webpackChunkName: 'admin-timings' */ '../pages/admin/AdminViews/Timings/Timings'));
const Availabilities = lazy(() => import(/* webpackChunkName: 'admin-availabilities' */ '../pages/admin/AdminViews/Availabilities/Availabilities'));
const FPCosts = lazy(() => import(/* webpackChunkName: 'admin-fp-costs' */ '../pages/admin/AdminViews/FP/Costs'));

const Clients = lazy(() => import(/* webpackChunkName: 'admin-clients' */ '../pages/admin/AdminViews/Clients/Clients'));
const AddClient = lazy(() => import(/* webpackChunkName: 'admin-clients-add' */ '../pages/admin/AdminViews/Clients/AddClient'));
const EditClient = lazy(() => import(/* webpackChunkName: 'admin-clients-edit' */ '../pages/admin/AdminViews/Clients/EditClient'));

const AuditLogs = lazy(() => import(/* webpackChunkName: 'admin-audit-logs' */ '../pages/admin/AdminViews/AuditLogs/AuditLogs'));
const ClientFP = lazy(() => import(/* webpackChunkName: 'admin-client-fp' */ '../pages/admin/AdminViews/ClientFP/ClientFP'));
const FuelRateDashboard = lazy(() => import(/* webpackChunkName: 'admin-fuel-rates' */ '../pages/admin/AdminViews/FuelRates/FuelRateDashboard'));

const AugmentAddress = lazy(() => import(/* webpackChunkName: 'admin-augment-address' */ '../pages/admin/AdminViews/AugmentAddress/AugmentAddress'));
const AddAugmentAddress = lazy(() => import(/* webpackChunkName: 'admin-augment-address-add' */ '../pages/admin/AdminViews/AugmentAddress/AddAugmentAddress'));
const EditAugmentAddress = lazy(() => import(/* webpackChunkName: 'admin-augment-address-edit' */ '../pages/admin/AdminViews/AugmentAddress/EditAugmentAddress'));

const RoutingRules = lazy(() => import(/* webpackChunkName: 'admin-routing-rules' */ '../pages/admin/AdminViews/RoutingRules/RoutingRules'));
const AddRoutingRule = lazy(() => import(/* webpackChunkName: 'admin-routing-rules-add' */ '../pages/admin/AdminViews/RoutingRules/AddRoutingRule'));
const EditRoutingRule = lazy(() => import(/* webpackChunkName: 'admin-routing-rules-edit' */ '../pages/admin/AdminViews/RoutingRules/EditRoutingRule'));

const PrinterProfiles = lazy(() => import(/* webpackChunkName: 'admin-printer-profiles' */ '../pages/admin/AdminViews/PrinterProfiles/PrinterProfiles'));
const AddPrinterProfile = lazy(() => import(/* webpackChunkName: 'admin-printer-profiles-add' */ '../pages/admin/AdminViews/PrinterProfiles/AddPrinterProfile'));
const EditPrinterProfile = lazy(() => import(/* webpackChunkName: 'admin-printer-profiles-edit' */ '../pages/admin/AdminViews/PrinterProfiles/EditPrinterProfile'));

const ClientEmployees = lazy(() => import(/* webpackChunkName: 'admin-client-employees' */ '../pages/admin/AdminViews/ClientEmployees/ClientEmployees'));
const AddClientEmployee = lazy(() => import(/* webpackChunkName: 'admin-client-employees-add' */ '../pages/admin/AdminViews/ClientEmployees/AddClientEmployee'));
const EditClientEmployee = lazy(() => import(/* webpackChunkName: 'admin-client-employees-edit' */ '../pages/admin/AdminViews/ClientEmployees/EditClientEmployee'));

const QuickViewLogs = lazy(() => import(/* webpackChunkName: 'admin-logs-quickview' */ '../pages/admin/AdminViews/Logs/QuickView'));
const DownloadLogs = lazy(() => import(/* webpackChunkName: 'admin-logs-download' */ '../pages/admin/AdminViews/Logs/Download'));
const DMEDownloadLogs = lazy(() => import(/* webpackChunkName: 'admin-dme-logs-download' */ '../pages/admin/AdminViews/DMELogs/Download'));

const QuoteReport = lazy(() => import(/* webpackChunkName: 'admin-reports-quote' */ '../pages/admin/AdminViews/Reports/QuoteReport'));
const ClientEvents = lazy(() => import(/* webpackChunkName: 'admin-client-events' */ '../pages/admin/AdminViews/ClientEvents/ClientEvents'));

// Customer Dashboard
const CustomerLogin = lazy(() => import(/* webpackChunkName: 'customer-login' */ '../pages/admin/CustomerViews/Login'));
const CustomerDashboard = lazy(() => import(/* webpackChunkName: 'customer-dashboard' */ '../pages/admin/CustomerViews/Dashboard'));

const CustomerClientEmployees = lazy(() => import(/* webpackChunkName: 'customer-client-employees' */ '../pages/admin/CustomerViews/ClientEmployees/ClientEmployees'));
const CustomerAddClientEmployee = lazy(() => import(/* webpackChunkName: 'customer-client-employees-add' */ '../pages/admin/CustomerViews/ClientEmployees/AddClientEmployee'));
const CustomerEditClientEmployee = lazy(() => import(/* webpackChunkName: 'customer-client-employees-edit' */ '../pages/admin/CustomerViews/ClientEmployees/EditClientEmployee'));

const CustomerClientProductList = lazy(() => import(/* webpackChunkName: 'customer-client-product-list' */ '../pages/admin/CustomerViews/ClientProduct/List'));
const CustomerClientProductImport = lazy(() => import(/* webpackChunkName: 'customer-client-product-import' */ '../pages/admin/CustomerViews/ClientProduct/Import'));

const ClientRas = lazy(() => import(/* webpackChunkName: 'customer-client-ras' */ '../pages/admin/CustomerViews/ClientRas'));
const ClientRasAction = lazy(() => import(/* webpackChunkName: 'customer-client-ras-action' */ '../pages/admin/CustomerViews/ClientRas/ClientRasAction'));
const ClientSubClient = lazy(() => import(/* webpackChunkName: 'customer-client-subclient' */ '../pages/admin/CustomerViews/ClientSubClient/List'));
const AddressBook = lazy(() => import(/* webpackChunkName: 'customer-address-book' */ '../pages/admin/CustomerViews/AddressBook'));


class AppRouter extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isShowNotificationsSlider: false,
        };
    }

    static propTypes = {
        // Prop funcs
        getNotificationCount: PropTypes.func.isRequired,
    };

    componentDidMount() {
        this.props.getNotificationCount();

        this.notificationCountInterval = setInterval(() => {
            if (!document.hidden) {
                this.props.getNotificationCount();
            }
        }, 10000);
    }

    componentWillUnmount() {
        clearInterval(this.notificationCountInterval);
    }

    toggleNotificationsSlider = () => {
        this.setState(prevState => ({
            isShowNotificationsSlider: !prevState.isShowNotificationsSlider,
        }));
    };

    openNotificationsSlider = () => {
        this.setState({
            isShowNotificationsSlider: true,
        });
    };

    closeNotificationsSlider = () => {
        this.setState({
            isShowNotificationsSlider: false,
        });
    };

    handleNotificationClick = (notification) => {
        if (!notification) {
            return;
        }

        this.closeNotificationsSlider();

        const params = new URLSearchParams();
        console.log('Notification is clicked. BookingId:', notification.booking_id);
        params.set(
            'bookingId',
            notification.booking_id + 15000
        );

        if (
            notification.type === 'zendesk_ticket'
            || notification.type === 'zendesk_comment'
        ) {
            params.set('comms', 'tickets');

            if (notification.zendesk_ticket_id) {
                console.log('Notification is clicked. TicketId:', notification.zendesk_ticket_id);
                params.set(
                    'ticketId',
                    notification.zendesk_ticket_id
                );
            }

            // if (notification.comment_id) {
            //     params.set(
            //         'commentId',
            //         notification.comment_id
            //     );
            // }
        } else if (
            notification.type === 'status_update'
        ) {
            params.set('comms', 'standard');
        }

        console.log('Notification is clicked. Page will be redirected to:', `/booking?${params.toString()}`);

        history.push(
            `/booking?${params.toString()}`
        );
    };

    clearNotificationTarget = () => {
        this.setState({
            notificationTarget: null,
        });
    };

    render() {
        const {
            isShowNotificationsSlider,
        } = this.state;

        return (
            <Router history={history}>
                <Fragment>
                    <AppChrome>
                        <Suspense fallback={null}>
                            <Header />
                        </Suspense>
                    </AppChrome>

                    <Suspense fallback={null}>
                        <Switch>
                            <Route exact path='/' component={HomePage} />
                            <PrivateRoute exact path='/home' component={HomePage} />

                            <Route exact path='/files' component={FilesPage} />
                            <Route exact path='/login' component={LoginPage} />
                            <Route exact path='/forgot-password' component={ForgotPasswordPage} />
                            <Route path='/reset-password' component={ResetPasswordPage} />
                            <Route path='/unsubscribe' component={UnsubscribePage} />

                            <Route exact path='/bok' component={BokPage} />
                            <Route exact path='/price/:id' component={BokPricePage} />
                            <Route exact path='/order/:id' component={BokPricePage} />
                            <Route exact path='/bid/:id' component={BidPricePage} />
                            <Route exact path='/bid-decline/:id' component={BidPricePage} />
                            <Route exact path='/approve/:id' component={BidPricePage} />
                            <Route exact path='/disapprove-hold/:id' component={BidPricePage} />
                            <Route exact path='/disapprove-disposal/:id' component={BidPricePage} />
                            <Route exact path='/confirm/:id' component={BidPricePage} />
                            <Route exact path='/status/:id' component={BokStatusPage} />
                            <Route exact path='/label/:id' component={LabelPage} />

                            <PrivateRoute
                                exact
                                path='/warehouse-label'
                                component={WarehouseLabelPage}
                            />

                            <PrivateRoute
                                exact
                                path='/booking'
                                render={props => (
                                    <BookingPage
                                        {...props}
                                        openNotificationsSlider={this.openNotificationsSlider}
                                    />
                                )}
                            />

                            <PrivateRoute
                                exact
                                path='/create-a-booking'
                                component={NewBookingPage}
                            />

                            <PrivateRoute
                                exact
                                path='/bookinglines'
                                component={BookingLinesPage}
                            />

                            <PrivateRoute
                                exact
                                path='/bookinglinedetails'
                                component={BookingLineDetailsPage}
                            />

                            <PrivateRoute
                                exact
                                path='/allbookings'
                                render={props => (
                                    <AllBookingsPage
                                        {...props}
                                        openNotificationsSlider={this.openNotificationsSlider}
                                    />
                                )}
                            />

                            <PrivateRoute
                                exact
                                path='/consolidation-workbench'
                                component={ConsolidationWorkbenchPage}
                            />

                            <PrivateRoute
                                exact
                                path='/consolidation/clusters/:id'
                                component={ConsolidationClusterDetailPage}
                            />

                            <PrivateRoute
                                exact
                                path='/bookingsets'
                                render={props => (
                                    <BookingSetsPage
                                        {...props}
                                        openNotificationsSlider={this.openNotificationsSlider}
                                    />
                                )}
                            />

                            <PrivateRoute
                                exact
                                path='/labels/bulk-view/:id'
                                render={props => (
                                    <BookingSetsViewPage
                                        {...props}
                                        openNotificationsSlider={this.openNotificationsSlider}
                                    />
                                )}
                            />

                            <PrivateRoute
                                exact
                                path='/upload'
                                component={UploadPage}
                            />

                            <PrivateRoute
                                exact
                                path='/pods'
                                component={PodsPage}
                            />

                            <PrivateRoute
                                exact
                                path='/reports'
                                render={props => (
                                    <ReportPage
                                        {...props}
                                        openNotificationsSlider={this.openNotificationsSlider}
                                    />
                                )}
                            />

                            <Main>
                                <Route exact path='/admin' component={Login} />
                                <Route exact path='/admin/login' component={Login} />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/dashboard'
                                    component={Dashboard}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/users'
                                    component={Users}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/users/add'
                                    component={AddUser}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/users/edit/:id'
                                    component={EditUser}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/emails'
                                    component={EmailTemplates}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/emails/add'
                                    component={AddEmailTemplates}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/emails/edit/:id'
                                    component={EditEmailTemplates}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/providers'
                                    component={FreightProviders}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/providers/add'
                                    component={AddFreightProviders}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/providers/edit/:id'
                                    component={EditFreightProviders}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/providers/statusmapping'
                                    component={StatusMapping}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/pricing-only'
                                    component={PricingOnlyList}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/pricing-only/upload'
                                    component={PricingOnlyUpload}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/vehicles'
                                    component={Vehicles}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/timings'
                                    component={Timings}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/availabilities'
                                    component={Availabilities}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/fp-costs'
                                    component={FPCosts}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/pricing-rule'
                                    component={PricingRuleList}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/pricing-rule/upload'
                                    component={PricingRuleUpload}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/pricing-rule/status'
                                    component={PricingRuleStatus}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/clients'
                                    component={Clients}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/clients/add'
                                    component={AddClient}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/clients/edit/:id'
                                    component={EditClient}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/audit-logs'
                                    component={AuditLogs}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/client-fp'
                                    component={ClientFP}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/fuel-rates'
                                    component={FuelRateDashboard}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/chart/totaldeliveries'
                                    component={TotalDeliveries}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/chart/ontimelatedeliveries'
                                    component={OnTimeLateDeliveries}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/chart/deliveriesbyclient'
                                    component={DeliveriesByClient}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/chart/activebooking'
                                    component={ActiveBookings}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/augmentaddress'
                                    component={AugmentAddress}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/augmentaddress/add'
                                    component={AddAugmentAddress}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/augmentaddress/edit/:id'
                                    component={EditAugmentAddress}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/routing-rules'
                                    component={RoutingRules}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/routing-rules/add'
                                    component={AddRoutingRule}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/routing-rules/edit/:id'
                                    component={EditRoutingRule}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/printer-profiles'
                                    component={PrinterProfiles}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/printer-profiles/add'
                                    component={AddPrinterProfile}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/printer-profiles/edit/:id'
                                    component={EditPrinterProfile}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/clientemployees'
                                    component={ClientEmployees}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/clientemployees/add'
                                    component={AddClientEmployee}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/clientemployees/edit/:id'
                                    component={EditClientEmployee}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/logs/quickview'
                                    component={QuickViewLogs}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/logs/download'
                                    component={DownloadLogs}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/dme_logs/download'
                                    component={DMEDownloadLogs}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/reports/quote'
                                    component={QuoteReport}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/admin/client-events'
                                    component={ClientEvents}
                                />

                                <Route
                                    exact
                                    path='/customerdashboard'
                                    component={CustomerLogin}
                                />

                                <Route
                                    exact
                                    path='/customerdashboard/login'
                                    component={CustomerLogin}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/customerdashboard/dashboard'
                                    component={CustomerDashboard}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/customerdashboard/client-ras'
                                    component={ClientRas}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/customerdashboard/client-ras/add'
                                    component={ClientRasAction}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/customerdashboard/client-ras/edit/:id'
                                    component={ClientRasAction}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/customerdashboard/client-ras/duplicate/:id'
                                    component={ClientRasAction}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/customerdashboard/clientemployees'
                                    component={CustomerClientEmployees}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/customerdashboard/clientemployees/add'
                                    component={CustomerAddClientEmployee}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/customerdashboard/clientemployees/edit/:id'
                                    component={CustomerEditClientEmployee}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/customerdashboard/clientemployees/edit/:id'
                                    component={CustomerEditClientEmployee}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/customerdashboard/clientproducts'
                                    component={CustomerClientProductList}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/customerdashboard/clientproducts/import'
                                    component={CustomerClientProductImport}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/customerdashboard/client-subclients'
                                    component={ClientSubClient}
                                />

                                <AdminPrivateRoute
                                    exact
                                    path='/customerdashboard/address-book'
                                    component={AddressBook}
                                />

                                <AdminPrivateRoute exact path='/admin/address-book' component={AddressBook} />
                            </Main>

                            <Redirect to='/' />
                        </Switch>
                    </Suspense>

                    <AppChrome>
                        <Suspense fallback={null}>
                            <Footer />
                        </Suspense>
                    </AppChrome>

                    <LazyMount
                        rootMargin='300px'
                        once={true}
                    >
                        <Suspense fallback={null}>
                            <ChatWidget />
                        </Suspense>
                    </LazyMount>

                    <NotificationSlider
                        isOpen={isShowNotificationsSlider}
                        toggleNotificationsSlider={
                            this.toggleNotificationsSlider
                        }
                        onNotificationClick={
                            this.handleNotificationClick
                        }
                    />
                </Fragment>
            </Router>
        );
    }
}

const mapDispatchToProps = dispatch => ({
    getNotificationCount: () => dispatch(getNotificationCount()),
});

export default connect(
    null,
    mapDispatchToProps
)(AppRouter);



// WEBPACK FOOTER //
// ./src/routers/AppRouter.js